import React from 'react';
import PropTypes from 'prop-types';
import { Box, Popover, Drawer, useMediaQuery, Divider } from '@mui/material';
import { m } from 'framer-motion';
import { COLORS } from './styles';

const MOBILE_BREAKPOINT_PX = 768;

const sheetPaperSx = {
    borderRadius: '10px',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.14), 0 1px 3px rgba(0, 0, 0, 0.06)',
    minWidth: 200,
    overflow: 'hidden',
};

const drawerPaperSx = {
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    paddingBottom: 'env(safe-area-inset-bottom, 8px)',
    backgroundColor: COLORS.paper,
};

const renderSlot = (slot) => {
    if (slot == null || slot === false) return null;
    if (Array.isArray(slot)) {
        return slot.map((node, idx) => (node == null
            ? null
            // eslint-disable-next-line react/no-array-index-key
            : <React.Fragment key={node?.key ?? idx}>{node}</React.Fragment>));
    }
    return slot;
};

const hasContent = (slot) => {
    if (slot == null || slot === false) return false;
    if (Array.isArray(slot)) return slot.some(node => node != null && node !== false);
    return true;
};

const MessageActionSheet = ({ open, anchorEl, onClose, slots = {} }) => {
    const isMobile = useMediaQuery(`(max-width:${MOBILE_BREAKPOINT_PX - 1}px)`);

    const reactionBar = slots?.reactionBar;
    const primaryActions = slots?.primaryActions;
    const destructiveActions = slots?.destructiveActions;

    const showReactionBar = hasContent(reactionBar);
    const showPrimary = hasContent(primaryActions);
    const showDestructive = hasContent(destructiveActions);

    const content = (
        <Box
            role="menu"
            aria-label="Message actions"
            sx={{ display: 'flex', flexDirection: 'column', py: 0.5 }}
        >
            {showReactionBar && (
                <Box sx={{ px: 1, py: 0.75 }}>
                    {renderSlot(reactionBar)}
                </Box>
            )}
            {showReactionBar && (showPrimary || showDestructive) && (
                <Divider sx={{ my: 0.25 }} />
            )}
            {showPrimary && (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {renderSlot(primaryActions)}
                </Box>
            )}
            {showPrimary && showDestructive && (
                <Divider sx={{ my: 0.25 }} />
            )}
            {showDestructive && (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {renderSlot(destructiveActions)}
                </Box>
            )}
        </Box>
    );

    if (isMobile) {
        return (
            <Drawer
                anchor="bottom"
                open={open}
                onClose={onClose}
                slotProps={{ paper: { sx: drawerPaperSx } }}
                ModalProps={{ keepMounted: false }}
            >
                <m.div
                    initial={{ y: 100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.2, ease: [0.2, 0.9, 0.3, 1] }}
                >
                    <Box
                        sx={{
                            width: 36,
                            height: 4,
                            borderRadius: 2,
                            backgroundColor: COLORS.divider,
                            mx: 'auto',
                            mt: 1,
                            mb: 0.5,
                        }}
                    />
                    {content}
                </m.div>
            </Drawer>
        );
    }

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{ paper: { sx: sheetPaperSx } }}
        >
            {content}
        </Popover>
    );
};

MessageActionSheet.propTypes = {
    open: PropTypes.bool.isRequired,
    anchorEl: PropTypes.any,
    onClose: PropTypes.func.isRequired,
    slots: PropTypes.shape({
        reactionBar: PropTypes.node,
        primaryActions: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
        destructiveActions: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
    }),
};

export default MessageActionSheet;
